import {
    PrePpWlDescriptionOfDevelopmentDevelopmentTypes,
    PrePpWlNonResidentialChangeOfUseTypes,
    PrePpWlTypeOfOtherDevelopmentTypes,
    PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes,
} from "@shared/prePublicPortalWestLindsay/contracts/IPrePpWlForm";
import { PrPpWlFormManager } from "@shared/prePublicPortalWestLindsay/managers/PrePpWlFormManager";

export class PrePpWlFormFeeCalculator {
    public static _fakeFeeAmount: number | null = null;
    public static setFakeFeeAmount(amount: number): void {
        this._fakeFeeAmount = amount;
    }
    public static calculateFee(req: CalculateFeeRequest, priceList: WlPriceListJson): IPreFormCalculateFeeResult {
        if (this._fakeFeeAmount !== null) {
            return { fee: this._fakeFeeAmount, isCustomFee: false };
        }
        if (!priceList) {
            return { fee: 0, isCustomFee: false };
        }
        const typeOfDevelopment = req.typeOfDevelopment;
        const serviceLevel = req.serviceLevel;
        const customFeeResult = { fee: 0, isCustomFee: true };

        if (!typeOfDevelopment) {
            return { fee: 0, isCustomFee: false };
        }

        switch (typeOfDevelopment) {
            case PrePpWlDescriptionOfDevelopmentDevelopmentTypes.ALTERATIONS_EXTENSIONS:
                return { fee: priceList.ALTERATIONS_OR_EXTENSIONS, isCustomFee: false };
            case PrePpWlDescriptionOfDevelopmentDevelopmentTypes.NEW_DWELLINGS:
                return this.tableBFeeCalculator(priceList, req.totalNumberOfUnitsProposed);
            case PrePpWlDescriptionOfDevelopmentDevelopmentTypes.RETAIL_OFFICE_INDUSTRIAL:
                if (PrPpWlFormManager.isAdditionalGrossFloorSpaceType(serviceLevel)) {
                    const floorSpace = Number(req.additionalGrossFloorSpace);
                    return this.tableCFloorSpaceFeeCalculator(priceList, floorSpace);
                } else if (serviceLevel === PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.CHANGE_OF_USE) {
                    const changeOfUse = req.nonResidentialChangeOfUse;
                    return this.tableCChangeOfUseFeeCalculator(priceList, changeOfUse);
                } else if (serviceLevel === PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.ALTERATIONS) {
                    return { fee: priceList.TABLE_C_ALTERATIONS, isCustomFee: false };
                } else {
                    return customFeeResult;
                }
            case PrePpWlDescriptionOfDevelopmentDevelopmentTypes.OTHER:
                return this.tableDFeeCalculator(priceList, serviceLevel);
            default:
                return customFeeResult;
        }
    }

    private static tableBFeeCalculator = (priceList: WlPriceListJson, numberOfUnits?: string): IPreFormCalculateFeeResult => {
        if (!numberOfUnits) {
            return { fee: 0, isCustomFee: true };
        }

        // Check if TABLE_B_FEE exists
        if (!priceList.TABLE_B_FEE) {
            return { fee: 0, isCustomFee: true };
        }

        const priceListFee = priceList.TABLE_B_FEE[numberOfUnits as keyof WlPriceListJson["TABLE_B_FEE"]];
        if (!priceListFee) {
            return { fee: 0, isCustomFee: true };
        }
        return { fee: priceListFee, isCustomFee: false };
    };

    private static tableCFloorSpaceFeeCalculator = (priceList: WlPriceListJson, floorSpace?: number): IPreFormCalculateFeeResult => {
        if (!floorSpace) {
            return { fee: 0, isCustomFee: true };
        }
        const floorSpaceString = `${Math.floor(floorSpace / 100) * 100}-${Math.ceil(floorSpace / 100) * 100 - 1}` as NumberRangeString;

        // Check if TABLE_C_FLOOR_SPACE_FEE exists and if the floorSpaceString is a valid key
        if (!priceList.TABLE_C_FLOOR_SPACE_FEE) {
            return { fee: 0, isCustomFee: true };
        }

        const priceListFee = priceList.TABLE_C_FLOOR_SPACE_FEE[floorSpaceString as keyof WlPriceListJson["TABLE_C_FLOOR_SPACE_FEE"]];
        if (priceListFee) {
            return { fee: priceListFee, isCustomFee: false };
        }
        return { fee: 0, isCustomFee: true };
    };

    private static tableCChangeOfUseFeeCalculator = (priceList: WlPriceListJson, changeOfUse?: string | null): IPreFormCalculateFeeResult => {
        // Check if TABLE_C_CHANGE_OF_USE_FEE exists
        if (!priceList.TABLE_C_CHANGE_OF_USE_FEE) {
            return { fee: 0, isCustomFee: true };
        }

        if (changeOfUse === PrePpWlNonResidentialChangeOfUseTypes.UNDER_1000) {
            return { fee: priceList.TABLE_C_CHANGE_OF_USE_FEE.UNDER_1000, isCustomFee: false };
        } else if (changeOfUse === PrePpWlNonResidentialChangeOfUseTypes.OVER_1000) {
            return { fee: priceList.TABLE_C_CHANGE_OF_USE_FEE.OVER_1000, isCustomFee: false };
        }
        return { fee: 0, isCustomFee: true };
    };

    private static tableDFeeCalculator = (priceList: WlPriceListJson, serviceLevel?: string): IPreFormCalculateFeeResult => {
        if (!serviceLevel) {
            return { fee: 0, isCustomFee: false };
        }

        // Check if TABLE_D_FEE exists
        if (!priceList.TABLE_D_FEE) {
            return { fee: 0, isCustomFee: true };
        }

        switch (serviceLevel) {
            case PrePpWlTypeOfOtherDevelopmentTypes.VARIATION_OR_REMOVAL_OF_CONDITION:
            case PrePpWlTypeOfOtherDevelopmentTypes.ADVERTISEMENTS: {
                return { fee: priceList.TABLE_D_FEE.VARIATION_OR_REMOVAL_CONDITION, isCustomFee: false };
            }
            case PrePpWlTypeOfOtherDevelopmentTypes.NON_HOUSEHOLDER_LISTED_BUILDING_CONSENT: {
                return { fee: priceList.TABLE_D_FEE.NON_HOUSEHOLDER_LISTED_BUILDING_CONSENT, isCustomFee: false };
            }
            default:
                return { fee: 0, isCustomFee: true };
        }
    };
}

export interface CalculateFeeRequest {
    typeOfDevelopment?: string;
    serviceLevel?: string;
    totalNumberOfUnitsProposed?: string;
    additionalGrossFloorSpace?: string;
    nonResidentialChangeOfUse?: string | null;
}

interface IPreFormCalculateFeeResult {
    fee: number;
    isCustomFee: boolean;
}

type NumberRangeString = `${number}-${number}`;

export interface WlPriceListJson {
    ALTERATIONS_OR_EXTENSIONS: number;
    TABLE_B_FEE: {
        [unit: string]: number;
    };
    TABLE_C_FLOOR_SPACE_FEE: {
        [range: NumberRangeString]: number;
    };
    TABLE_C_CHANGE_OF_USE_FEE: {
        UNDER_1000: number;
        OVER_1000: number;
    };
    TABLE_C_ALTERATIONS: number;
    TABLE_D_FEE: {
        VARIATION_OR_REMOVAL_CONDITION: number;
        ADVERTISEMENTS: number;
        NON_HOUSEHOLDER_LISTED_BUILDING_CONSENT: number;
    };
    EXPIRATION_DATE: string;
}
